import { BetrokkenTrein, BusEvacuatie } from "@/entities/betrokken-treinen";
import { RoleGate, UserRole } from "@/features/auth";
import { BusEvacuatieNewEditDialog } from "@/features/betrokken-treinen/components/evacuatie-bus/bus-evacuatie-new-edit-dialog";
import {
    Badge,
    Button,
    Card,
    Empty,
    EmptyHeader,
    EmptyImage,
    EmptyTitle,
    MessageBar,
    ProgressCircleWithLabel,
    Separator,
} from "@/shared/components";
import { useState } from "react";
import { BusEvacuatieAfronden } from "./bus-evacuatie-afronden";
import BusEvacuatieCancelStatus from "./bus-evacuatie-cancel-status";
import BusEvacutatieActions from "./bus-evacutie-actions";
import { EvacuatieBusEtas } from "./evacuatie-bus-eta";
import { EvacuatieBusGewijzigdeVelden } from "./gewijzigde-velden";
import { OpstelLocatieEnAfzetStation } from "./opstel-locatie-en-afzet-station";

type BetrokkenTreinEvacuatieBusProps = {
    dossierId: string;
    trein: BetrokkenTrein;
};

function BetrokkenTreinEvacuatieBusAfronden({ dossierId, trein }: BetrokkenTreinEvacuatieBusProps) {
    const [afrondenDialogOpen, setAfrondenDialogOpen] = useState(false);

    if (BusEvacuatie.hasNoEtasOrIsCompleted(trein.busEvacuatie!)) {
        return null;
    }

    const { afzetStation, etaAfzetStation } = trein.busEvacuatie!;

    return (
        <>
            <RoleGate roles={["regievoerder"]}>
                <Button variant="secondary" size="s" onClick={() => setAfrondenDialogOpen(true)}>
                    Evacuatie afronden
                </Button>
            </RoleGate>
            {afrondenDialogOpen && (
                <BusEvacuatieAfronden
                    dossierId={dossierId}
                    open={afrondenDialogOpen}
                    onOpenChange={setAfrondenDialogOpen}
                    trein={trein}
                    defaultValues={{ naarStation: afzetStation, etaNaarStation: etaAfzetStation }}
                />
            )}
        </>
    );
}

export function BetrokkenTreinEvacuatieBusToevoegen({ dossierId, trein }: BetrokkenTreinEvacuatieBusProps) {
    const [evacuatiebusDialogOpen, setEvacuatiebusDialogOpen] = useState(false);

    return (
        <>
            <Button
                icon="bus-alt"
                iconStrokeWidth="normal"
                iconSize="xs"
                variant="tertiary"
                className="text-s order-2 col-span-1"
                onClick={() => setEvacuatiebusDialogOpen(true)}
            >
                Evacuatiebus bestellen
            </Button>
            {evacuatiebusDialogOpen && (
                <BusEvacuatieNewEditDialog
                    dossierId={dossierId}
                    trein={trein}
                    open={evacuatiebusDialogOpen}
                    onOpenChange={setEvacuatiebusDialogOpen}
                />
            )}
        </>
    );
}

export function BetrokkenTreinEvacuatieBus({ dossierId, trein }: Readonly<BetrokkenTreinEvacuatieBusProps>) {
    if (!trein.busEvacuatie) {
        return (
            <RoleGate roles={[UserRole.DEWIT, UserRole.TRANSVISION]}>
                <Card className="col-span-3">
                    <Empty>
                        <EmptyHeader className="flex justify-center font-normal">
                            <EmptyImage />
                            <EmptyTitle className="text-s text-content-subtle">Geen evacuatiebus aangevraagd</EmptyTitle>
                        </EmptyHeader>
                    </Empty>
                </Card>
            </RoleGate>
        );
    }

    const { aantalBussen, etas, afzetStation, etaAfzetStation, opstelLocatie, status } = trein.busEvacuatie;
    const isComplete = (etas?.length ?? 0) >= aantalBussen && aantalBussen > 0;

    return (
        <div className="border-default flex w-full flex-col gap-4 rounded-l border px-4 py-2">
            <div className="flex items-center justify-between">
                <div className="flex gap-4">
                    <h3 className="leading-m text-l font-medium">Evacuatiebus</h3>
                    <Badge variant="blue-subtle">{BusEvacuatie.formatUitvoerder(trein.busEvacuatie)}</Badge>
                </div>
                <RoleGate roles={[UserRole.REGIEVOERDER]}>
                    {BusEvacuatie.hasActionsAvailable(trein.busEvacuatie) && <BusEvacutatieActions dossierId={dossierId} trein={trein} />}
                </RoleGate>
            </div>
            <div className="grid grid-cols-[1fr_min-content_1fr] items-start gap-x-6 gap-y-4">
                <ProgressCircleWithLabel
                    className="text-content-success"
                    total={aantalBussen}
                    current={etas?.length ?? 0}
                    label="Geleverd"
                    unit="bussen"
                    button={<BetrokkenTreinEvacuatieBusAfronden dossierId={dossierId} trein={trein} />}
                    type={isComplete ? "check" : "default"}
                />
                {status === "Afgerond" && (
                    <>
                        <Separator orientation="vertical" className="border-default" />
                        <div className="grid gap-2">
                            <label className="leading-l text-s text-content-emphasis font-bold">Evacuatie afgerond</label>
                            <div className="grid">
                                <label className="leading-l text-s text-content-emphasis font-bold">ETA op afzetstation</label>
                                <span>{etaAfzetStation}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <RoleGate roles={["dewit", "transvision"]}>
                <EvacuatieBusGewijzigdeVelden dossierId={dossierId} treinId={trein.id} />
                {status === "Afgerond" && (
                    <MessageBar>
                        De evacuatie is <span className="font-bold">afgerond</span>. Bussen zijn onderweg naar het afzetstation.
                    </MessageBar>
                )}
            </RoleGate>
            <BusEvacuatieCancelStatus dossierId={dossierId} treinId={trein.id} status={trein.busEvacuatie.status} />
            <div className="grid grid-cols-[1fr_min-content_1fr] items-start gap-x-6 gap-y-4">
                <EvacuatieBusEtas dossierId={dossierId} trein={trein} etas={etas} />
                <Separator orientation="vertical" className="border-default" />
                <OpstelLocatieEnAfzetStation opstelLocatie={opstelLocatie} afzetStation={afzetStation} />
            </div>
        </div>
    );
}
